import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { Observable, Subscription } from "rxjs";
import { select, Store } from "@ngrx/store";
import {
  getContractExcelList,
  getContractList,
  resetContractsState,
} from "src/app/store/actions/contracts/contracts.actions";
import { AppState, contractsStateTableConfig, loadedMenusState } from "src/app/store/reducers";

import { MenuDTO, RouteData } from "src/app/models/Menu/MenuDTO";
import {
  TableConfig,
  FieldType,
  FormatType,
  FilterEvent,
  PageEvent,
  SearchEvent,
  SortEvent,
  DefaultPage,
  ExportEvent,
  TableEvent,
  ColumnSorterInfo,
} from "src/app/models/custom-table/custom-table.model";
import { subMenusIterator } from "src/app/shared/utils/menuUtils";

import { BaseContractsList } from "src/app/shared/base-classes/base-contracts-list/base-contracts-list";

const CONTRACT_USAGE_CODE = "CONTRACT";

@Component({
  selector: "app-contract-list",
  templateUrl: "./contract-list.component.html",
  styleUrls: ["./contract-list.component.scss"],
})
export class ContractListComponent
  extends BaseContractsList
  implements OnInit, OnDestroy
{
  isLoading$: Observable<boolean>;

  contractList$: Observable<any[]>;
  excelContractList$: Observable<any[]>;
  totalContracts$: Observable<number>;

  loadedContractSub: Subscription;
  contractList: any[];

  menus$: Observable<MenuDTO[]>;
  menuSub: Subscription;

  formPath: string = "contracts/contract";
  tableTitle: string;
  headerIcon: string;

  tableConfig: TableConfig[] = [
    {
      header: "CustomGrid.Options",
      field: FieldType.OPTIONS,
      fieldType: FieldType.OPTIONS,
    },
    {
      header: "Contracts.contractOverallStatusDescription",
      field: "contractOverallStatusDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Contracts.code",
      field: "code",
      fieldType: FieldType.STRING,
      identifier: true,
      hidden: true,
    },
    {
      header: "Contracts.ContractNumber",
      field: "contractNumber",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.Description",
      field: "description",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.Status",
      field: "isEnabled",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Contracts.PlafondRuleCode",
      field: "plafond.plafondRuleDescription",
      fieldType: FieldType.CHIP_STRING,
    },
    {
      header: "Contracts.AvailablePlafondPercentage",
      field: "plafond.availablePercentage",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.AvailablePlafond",
      field: "availablePlafond",
      fieldType: FieldType.PROGRESS,
      hidden: false,
    },
    {
      header: "Contracts.CurrentlyOnHand",
      field: "plafond.currentlyOnHand",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.ReservedPlafond",
      field: "plafond.reservedPlafond",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.CurrentlyUsed",
      field: "plafond.currentlyUsed",
      fieldType: FieldType.NUMBER,
      hidden: true,
    },
    {
      header: "Contracts.Plafond",
      field: "plafond.plafond",
      fieldType: FieldType.STRING,
    },
    {
      header: "Contracts.ToleranceAllowed",
      field: "plafond.isToleranceAllowed",
      fieldType: FieldType.BOOLEAN,
      hidden: true,
    },
    {
      header: "Contracts.TolerancePlafond",
      field: "plafond.tolerancePlafond",
      fieldType: FieldType.NUMBER,
    },
    {
      header: "Contracts.CustomerCode",
      field: "customer.customerCode",
      fieldType: FieldType.STRING,
      searchable: true,
    },
    {
      header: "Contracts.CustomerVatin",
      field: "customer.vatin",
      fieldType: FieldType.STRING,
      hidden: true,
    },
    {
      header: "Contracts.CustomerName",
      field: "customer.name",
      fieldType: FieldType.STRING,
    },
    {
      header: "Contracts.StartedOn",
      field: "startedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Contracts.EndedOn",
      field: "endedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
    },
    {
      header: "Contracts.CreatedOn",
      field: "createdOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
    {
      header: "Contracts.UpdatedOn",
      field: "updatedOn",
      fieldType: FieldType.DATE_TIME,
      formatType: FormatType.DATETIME,
      hidden: true,
    },
  ];

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    store: Store<AppState>
  ) {
    super(store);

    this.columnInfo$ = this.store.pipe(select(contractsStateTableConfig));
  }

  ngOnInit(): void {
    this.menus$ = this.store.pipe(select(loadedMenusState));

    this.loadScreenTitle();
    this.getTableConfig(CONTRACT_USAGE_CODE);
    this.setTableCustomElements();
    this.getContracts();
  }

  loadScreenTitle() {
    this.menuSub = this.menus$.subscribe((menus: MenuDTO[]) => {
      if (menus && menus.length) {
        this.route.data.subscribe((routeData: RouteData) => {
          menus.forEach((menuEl: MenuDTO) => {
            const selectedMenu = subMenusIterator(menuEl, routeData);
            if (selectedMenu) {
              this.headerIcon = selectedMenu.icon;
              this.tableTitle = selectedMenu.description;
              this.titleService.setTitle(this.tableTitle);
            }
          });
        });
      }
    });
  }

  getContracts(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getContractList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.PAGE_SIZE,
            realSize: true,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  getContractsExcelList(
    pageEvent?: PageEvent,
    sortEvent?: SortEvent,
    searchEvent?: SearchEvent,
    filterEvent?: FilterEvent
  ) {
    const { pageIndex, pageSize } = pageEvent || {};
    this.store.dispatch(
      getContractExcelList({
        options: {
          paging: {
            skip: (pageIndex * pageSize) | DefaultPage.PAGE_INDEX,
            top: pageSize ?? DefaultPage.EXCEL_PAGE_SIZE,
          },
          filteringArgs: searchEvent,
          sortingArgs: sortEvent,
          advancedFilteringArgs: filterEvent,
        },
      })
    );
  }

  onGetTableData(tableEvent: TableEvent) {
    this.getTableData(tableEvent, this.getContracts.bind(this));
  }

  onGetExportData(exportEvent: ExportEvent) {
    this.getExportData(exportEvent, this.getContractsExcelList.bind(this));
  }

  onSaveTableConfig(columnInfo: ColumnSorterInfo[]) {
    this.saveTableConfig(columnInfo, CONTRACT_USAGE_CODE);
  }

  ngOnDestroy() {
    if (this.menuSub) {
      this.menuSub.unsubscribe();
    }
    if (this.loadedContractSub) {
      this.loadedContractSub.unsubscribe();
    }
    this.store.dispatch(resetContractsState());
  }
}
